import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { OptionField } from 'components/Field/OptionField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  doEngageFlatsLoad,
  doEngageFlatsSetFilter,
} from 'store/remote/engageActions';
import {
  selectEnagageFlatsFilterissueContactTypes,
  selectEngageFlatsFilterOccupationStatuses,
  selectEngageFlatsRequiresResidentSupport,
} from 'store/remote/engageSelectors';
import {
  FlatIssueType,
  FlatRequiresResidentSupport,
} from 'types/DTOs/BlockManagerDTOs';
import {
  EngageFlatsDefaultFilter,
  EngageFlatsIssueTypeFilters,
  EngageFlatsOccupationStatusFilters,
  EngageFlatsRequiresResidentSupportFilters,
  FlatOccupationStatus,
  OccupationStatusIcons,
} from 'types/Engage';

const allOccupationStatusIds = EngageFlatsOccupationStatusFilters.map(
  (status) => status.id,
);

export const EngageFlatsFilterModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlatsFilter>
> = ({ route }) => {
  const dispatch = useDispatch();
  const { pageKey, propertyId } = route.params;

  const currentOccupationStatuses = useSelector((state) =>
    selectEngageFlatsFilterOccupationStatuses(state, pageKey),
  );
  const currentissueContactTypes = useSelector((state) =>
    selectEnagageFlatsFilterissueContactTypes(state, pageKey),
  );
  const currentRequiresResidentSupport = useSelector((state) =>
    selectEngageFlatsRequiresResidentSupport(state, pageKey),
  );

  const [occupationStatuses, setOccupationStatuses] = React.useState(
    currentOccupationStatuses,
  );

  const [issueContactTypes, setissueContactTypes] = React.useState(
    currentissueContactTypes,
  );
  const [requiresResidentSupport, setRequiresResidentSupport] = React.useState(
    currentRequiresResidentSupport,
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const handleApply = React.useCallback(() => {
    dispatch(
      doEngageFlatsSetFilter({
        key: pageKey,
        data: {
          occupationStatuses,
          issueContactTypes,
          requiresResidentSupport,
        },
      }),
    );
    dispatch(
      doEngageFlatsLoad({
        key: pageKey,
        propertyId,
        page: 'reset',
      }),
    );
    handleClose();
  }, [
    dispatch,
    handleClose,
    occupationStatuses,
    issueContactTypes,
    requiresResidentSupport,
    pageKey,
    propertyId,
  ]);

  const handleClear = React.useCallback(() => {
    setOccupationStatuses(EngageFlatsDefaultFilter.occupationStatuses);
    setissueContactTypes(EngageFlatsDefaultFilter.issueContactTypes);
    setRequiresResidentSupport(
      EngageFlatsDefaultFilter.requiresResidentSupport,
    );
  }, []);

  const handleOccupationStatusChange = React.useCallback((values: string[]) => {
    setOccupationStatuses(values as FlatOccupationStatus[]);
  }, []);

  const handleIssueContactTypesChange = React.useCallback(
    (values: FlatIssueType[]) => {
      setissueContactTypes(values);
    },
    [],
  );

  const handleRequiresResidentSupportChange = React.useCallback(
    (values: FlatRequiresResidentSupport[]) => {
      setRequiresResidentSupport(values);
    },
    [],
  );

  const occupationBadgeActive =
    occupationStatuses.length !== allOccupationStatusIds.length;

  const issueContactTypesBadgeActive = issueContactTypes.length !== 0;

  const requiresResidentSupportBadgeActive =
    requiresResidentSupport.length !== 0;

  const isDefaultSelection =
    isEqual(occupationStatuses, EngageFlatsDefaultFilter.occupationStatuses) &&
    isEqual(issueContactTypes, EngageFlatsDefaultFilter.issueContactTypes) &&
    isEqual(
      requiresResidentSupport,
      EngageFlatsDefaultFilter.requiresResidentSupport,
    );

  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Flats Filter"
          onPressCancelButton={handleClose}
          cancelButtonText="Close"
          onPressSaveButton={handleApply}
          saveButtonText="Filter"
        />
      }
    >
      <Card marginBottomHalf>
        <OptionField
          label="Occupation Status"
          modalLabel="Occupation Status"
          onChange={handleOccupationStatusChange}
          value={occupationStatuses}
          multiSelect
          modalEnableSelectAll
          showBadge={occupationBadgeActive}
          options={EngageFlatsOccupationStatusFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
            icon: OccupationStatusIcons[status.id],
            iconSize: 22,
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
        <OptionField
          label="Issue Types"
          modalLabel="Issue Types"
          onChange={handleIssueContactTypesChange}
          value={issueContactTypes}
          multiSelect
          modalEnableSelectAll
          showBadge={issueContactTypesBadgeActive}
          options={EngageFlatsIssueTypeFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
        <OptionField
          label="Requires Resident Support"
          modalLabel="Requires Resident Support"
          onChange={handleRequiresResidentSupportChange}
          value={requiresResidentSupport}
          multiSelect
          modalEnableSelectAll
          showBadge={requiresResidentSupportBadgeActive}
          options={EngageFlatsRequiresResidentSupportFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
      </Card>

      <Button
        text="Clear Filters"
        buttonTheme={ButtonTheme.link}
        style={{
          alignSelf: 'center',
          marginVertical: theme.paddingPageVertical,
        }}
        onPress={handleClear}
        disabled={isDefaultSelection}
      />
    </PageScrollView>
  );
};
